// src/services/emailService.js
import axios from 'axios';
import emailjs from '@emailjs/browser';

// Initialize EmailJS (get credentials from https://www.emailjs.com/)
emailjs.init(process.env.REACT_APP_EMAILJS_PUBLIC_KEY);

export const sendContactEmail = async (formData, formType = 'contact') => {
  const { name, email, phone, company, service, message } = formData;

  const isEnrollment = formType === 'training';
  const headerTitle = isEnrollment ? 'New Training Enrollment' : 'New Contact Form Submission';
  const subjectPrefix = isEnrollment ? 'Training Enrollment' : 'New Contact Form';
  const companyFieldLabel = isEnrollment ? 'College / Degree:' : 'Company:';
  const serviceFieldLabel = isEnrollment ? 'Training Program:' : 'Service Interested In:';

  const htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <style>
        body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
        .container { max-width: 600px; margin: 0 auto; padding: 20px; background-color: #f8fafc; }
        .header { background: linear-gradient(135deg, #0d9488 0%, #0891b2 100%); color: white; padding: 30px; text-align: center; border-radius: 10px 10px 0 0; }
        .content { background-color: white; padding: 30px; border-radius: 0 0 10px 10px; box-shadow: 0 2px 4px rgba(0,0,0,0.1); }
        .field { margin-bottom: 20px; padding-bottom: 15px; border-bottom: 1px solid #e5e7eb; }
        .field:last-child { border-bottom: none; }
        .label { font-weight: bold; color: #0d9488; display: block; margin-bottom: 5px; }
        .value { color: #4b5563; }
        .message-box { background-color: #f0fdfa; padding: 15px; border-radius: 8px; border-left: 4px solid #0d9488; }
      </style>
    </head>
    <body>
      <div class="container">
        <div class="header">
          <h1 style="margin: 0;">${headerTitle}</h1>
          <p style="margin: 10px 0 0 0;">EDSY Tech Website</p>
        </div>
        <div class="content">
          <div class="field">
            <span class="label">Name:</span>
            <span class="value">${name}</span>
          </div>
          <div class="field">
            <span class="label">Email:</span>
            <span class="value">${email}</span>
          </div>
          ${phone ? `<div class="field"><span class="label">Phone:</span><span class="value">${phone}</span></div>` : ''}
          ${company ? `<div class="field"><span class="label">${companyFieldLabel}</span><span class="value">${company}</span></div>` : ''}
          <div class="field">
            <span class="label">${serviceFieldLabel}</span>
            <span class="value">${service}</span>
          </div>
          <div class="field">
            <span class="label">Message:</span>
            <div class="message-box">
              <p class="value" style="margin: 0; white-space: pre-wrap;">${message}</p>
            </div>
          </div>
          <div style="text-align: center; margin-top: 20px; padding-top: 20px; border-top: 1px solid #e5e7eb; color: #6b7280; font-size: 12px;">
            <p>Submitted on: ${new Date().toLocaleString()}</p>
          </div>
        </div>
      </div>
    </body>
    </html>
  `;

  const adminEmailParams = {
    to_email: process.env.REACT_APP_ADMIN_EMAIL || 'info@edsytech.com',
    to_name: 'EDSY Tech Team',
    from_name: name,
    from_email: 'info@edsytech.com',
    subject: `${subjectPrefix}: ${service} - ${name}`,
    message: htmlContent,
    phone: phone || 'Not provided',
    company: company || 'Not provided'
  };

  try {
    // Send to admin
    await emailjs.send(
      process.env.REACT_APP_EMAILJS_SERVICE_ID,
      process.env.REACT_APP_EMAILJS_ADMIN_TEMPLATE_ID,
      adminEmailParams
    );

    return {
      success: true,
      message: 'Thank you for contacting us! We will get back to you within 24 hours.',
      data: { status: 'success' }
    };

  } catch (error) {
    console.error('EmailJS Error:', error);
    throw new Error('Failed to send email. Please try again.');
  }
};

export const sendAutoReply = async (customerName, customerEmail) => {
  const htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <style>
        body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
        .container { max-width: 600px; margin: 0 auto; padding: 20px; background-color: #f8fafc; }
        .header { background: linear-gradient(135deg, #0d9488 0%, #0891b2 100%); color: white; padding: 40px; text-align: center; border-radius: 10px 10px 0 0; }
        .content { background-color: white; padding: 40px; border-radius: 0 0 10px 10px; }
        .contact-info { background-color: #f0fdfa; padding: 20px; border-radius: 8px; margin: 20px 0; }
      </style>
    </head>
    <body>
      <div class="container">
        <div class="header">
          <h1 style="margin: 0;">Thank You for Contacting EDSY Tech!</h1>
        </div>
        <div class="content">
          <p>Hi ${customerName},</p>
          <p>Thank you for reaching out! We have received your inquiry and our team will review it shortly.</p>
          <p><strong>What happens next?</strong></p>
          <ul>
            <li>Our team will review your request within 24 hours</li>
            <li>You'll receive a detailed response from one of our experts</li>
            <li>We'll discuss how we can help with your project</li>
          </ul>
          <div class="contact-info">
            <p style="margin: 0 0 10px 0;"><strong>Need immediate assistance?</strong></p>
            <p style="margin: 5px 0;">📧 Email: info@edsytech.com</p>
            <p style="margin: 5px 0;">📞 Phone: +1 (555) 123-4567</p>
          </div>
          <p>Best regards,<br><strong>The EDSY Tech Team</strong></p>
        </div>
      </div>
    </body>
    </html>
  `;

  const customerEmailParams = {
    to_email: customerEmail,
    to_name: customerName,
    from_name: 'EDSY Tech',
    message: htmlContent,
    subject: 'Thank you for contacting EDSY Tech!'
  };

  try {
    await emailjs.send(
      process.env.REACT_APP_EMAILJS_SERVICE_ID,
      process.env.REACT_APP_EMAILJS_CUSTOMER_TEMPLATE_ID,
      customerEmailParams
    );
    console.log('Auto-reply sent successfully');
  } catch (error) {
    console.error('Auto-reply error:', error);
  }
};